import { useEffect } from "react";
import AnfitriaoPerfilEditar from "./AnfitriaoPerfilEditar";

const FOTO_ESPACO_PLACEHOLDER = "/storage/images/placeholders/espaco-default.jpg";

const normalizarUrlMidia = (src?: string | null): string => {
  const valor = String(src || "").trim();

  if (!valor || valor === "0" || valor.toLowerCase() === "null" || valor.toLowerCase() === "undefined") {
    return "";
  }

  if (valor.startsWith("data:image")) {
    return valor;
  }

  if (valor.endsWith("espaco-default.jpg") || valor.includes("images/placeholders/espaco-default.jpg")) {
    return FOTO_ESPACO_PLACEHOLDER;
  }

  if (/^https?:\/\//i.test(valor)) {
    try {
      const url = new URL(valor);
      url.pathname = url.pathname
        .replace(/\/storage\/storage\//g, "/storage/")
        .replace(/\/storage\/public\//g, "/storage/");

      if (url.pathname.endsWith("espaco-default.jpg") || url.pathname.includes("images/placeholders/espaco-default.jpg")) {
        url.pathname = FOTO_ESPACO_PLACEHOLDER;
      }

      return url.toString();
    } catch {
      return valor;
    }
  }

  const normalizado = valor
    .replace(/^public\//, "")
    .replace(/^storage\//, "")
    .replace(/^\/storage\/storage\//, "/storage/")
    .replace(/^\/storage\/public\//, "/storage/");

  if (normalizado.endsWith("espaco-default.jpg") || normalizado.includes("images/placeholders/espaco-default.jpg")) {
    return FOTO_ESPACO_PLACEHOLDER;
  }

  if (normalizado.startsWith("/storage/") || normalizado.startsWith("/images/")) {
    return normalizado;
  }

  if (normalizado.startsWith("images/")) {
    return `/${normalizado}`;
  }

  return `/storage/${normalizado.replace(/^\/+/, "")}`;
};

const isPlaceholderEspaco = (src?: string | null): boolean =>
  String(src || "").includes("images/placeholders/espaco-default") || String(src || "").endsWith("espaco-default.jpg");

const isImagemRealSalva = (src?: string | null): boolean => {
  const valor = String(src || "");

  return Boolean(
    valor &&
      !valor.startsWith("data:image") &&
      !valor.includes("placeholder") &&
      !valor.includes("/images/placeholders/") &&
      !valor.includes("/storage/images/placeholders/")
  );
};

const aplicarPlaceholderComoBackground = (slot: Element): void => {
  const areaFoto = (slot.querySelector(".relative") || slot) as HTMLElement;

  areaFoto.style.backgroundImage = `url(${FOTO_ESPACO_PLACEHOLDER})`;
  areaFoto.style.backgroundSize = "cover";
  areaFoto.style.backgroundPosition = "center";
  areaFoto.dataset.allepetsPlaceholderEspaco = "1";
};

const removerPlaceholderComoBackground = (slot: Element): void => {
  const areaFoto = (slot.querySelector(".relative") || slot) as HTMLElement;

  areaFoto.style.backgroundImage = "";
  areaFoto.style.backgroundSize = "";
  areaFoto.style.backgroundPosition = "";
  delete areaFoto.dataset.allepetsPlaceholderEspaco;
};

const garantirLinkVer = (slot: Element, src: string): void => {
  if (!isImagemRealSalva(src)) {
    return;
  }

  const wrapper = slot.parentElement || slot;
  if (wrapper.querySelector("[data-allepets-link-ver-foto='1']")) {
    return;
  }

  const p = document.createElement("p");
  p.className = "mb-1 text-xs text-muted-foreground";
  p.dataset.allepetsLinkVerFoto = "1";

  const texto = document.createTextNode("Arquivo atual: ");
  const link = document.createElement("a");
  link.href = src;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.className = "text-coral underline";
  link.textContent = "ver";

  p.appendChild(texto);
  p.appendChild(link);
  slot.insertAdjacentElement("afterend", p);
};

const corrigirFotosEspaco = (): void => {
  const sections = Array.from(document.querySelectorAll("section"));
  const secaoApresentacao = sections.find((section) =>
    section.textContent?.includes("Apresentação") && section.textContent?.includes("Fotos do Espaço")
  );

  if (!secaoApresentacao) {
    return;
  }

  const tituloFotos = Array.from(secaoApresentacao.querySelectorAll("h4")).find((h4) =>
    h4.textContent?.includes("Fotos do Espaço") ||
    h4.textContent?.includes("Fotos do Local") ||
    h4.textContent?.includes("Fotos do atendimento")
  );

  const blocoFotos = tituloFotos?.parentElement;
  const grid = blocoFotos?.querySelector(".grid.grid-cols-2");

  if (!grid) {
    return;
  }

  const slots = Array.from(grid.children).slice(0, 4);

  slots.forEach((slot) => {
    const areaFoto = slot.querySelector(".relative") || slot;
    const img = areaFoto.querySelector("img") as HTMLImageElement | null;

    if (!img) {
      aplicarPlaceholderComoBackground(slot);
      return;
    }

    const srcAtual = img.getAttribute("src") || "";
    const srcNormalizado = normalizarUrlMidia(srcAtual);

    if (!srcNormalizado || isPlaceholderEspaco(srcNormalizado)) {
      aplicarPlaceholderComoBackground(slot);
      return;
    }

    removerPlaceholderComoBackground(slot);

    if (srcNormalizado !== srcAtual) {
      img.setAttribute("src", srcNormalizado);
    }

    if (!img.dataset.allepetsFallbackEspaco) {
      img.dataset.allepetsFallbackEspaco = "1";
      img.addEventListener("error", () => {
        aplicarPlaceholderComoBackground(slot);
      });
    }

    garantirLinkVer(areaFoto, srcNormalizado);
  });
};

const AnfitriaoPerfilEditarFotosFix = (props: { embedded?: boolean }) => {
  useEffect(() => {
    corrigirFotosEspaco();

    const observer = new MutationObserver(() => {
      window.requestAnimationFrame(corrigirFotosEspaco);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["src"],
    });

    return () => observer.disconnect();
  }, []);

  return <AnfitriaoPerfilEditar {...props} />;
};

export default AnfitriaoPerfilEditarFotosFix;
